/**
 * Vocabulary the Media Plans tab offers as suggestions. Free text is always
 * allowed — these just keep the common spellings consistent with how the
 * team writes plans (and how the engine's age-only demo waiver reads them).
 */

import type { AdvertiserPricingSummary, GuardrailKind, HistoryReferenceFlag, MediaPlanAgency, MediaPlanDoc, MediaPlanStatus, RateBasis, RateConfidence, UnitHistoryReference } from './types';
import type { StatusVariant } from '../../ui';
import { fmtUsd } from './formStyles';

export const CULTURE_STACKS = [
  'Music',
  'Hip-Hop & R&B',
  'Latino Culture',
  'Black Culture',
  'Comedy',
  'Sports',
  'Gaming',
  'Pop Culture & Celebrity Conversation',
  'Lifestyle',
  'Beauty & Fashion',
  'Food & Dining',
  'QSR',
  'Family & Parenting',
  'Faith & Inspiration',
  'Finance',
  'Automotive',
  'Tech',
  'Travel',
] as const;

export const DEMO_SUGGESTIONS = ['A18-34', 'A18-49', 'A25-54', 'W25-54', 'M18-49', 'P18+', 'AACM', 'Hispanic A18-49', 'Latino A25-54', 'Multicultural A18-49'] as const;

export const DAYPART_OPTIONS = ['Early morning 6–9am', 'Daytime 9am–4pm', 'Early fringe 4–7pm', 'Primetime 7–11pm', 'Late night 11pm–2am', 'Weekends'] as const;

export const GEO_SCOPE_LABELS: Record<'national' | 'dma' | 'state' | 'zip', string> = {
  national: 'National (US)',
  dma: 'DMAs',
  state: 'States',
  zip: 'ZIP codes',
};

/**
 * How each status reads in a list: the everyday states (draft, approved ✓)
 * stay quiet, shared — the one waiting on the client — is filled, and a
 * superseded version fades back.
 */
export const PLAN_STATUS_PILL: Record<MediaPlanStatus, { variant: StatusVariant; appearance: 'filled' | 'quiet'; faded?: boolean }> = {
  draft: { variant: 'draft', appearance: 'quiet' },
  shared: { variant: 'approved', appearance: 'filled' },
  approved: { variant: 'ok', appearance: 'quiet' },
  superseded: { variant: 'paused', appearance: 'quiet', faded: true },
};

/** The one wording for "no agency" in every agency picker. */
export const DIRECT_AGENCY_LABEL = 'Direct (no agency)';

/** The agency list's own "Direct (no agency)" entry. Every picker offers DIRECT_AGENCY_LABEL as its empty choice instead. */
export const DIRECT_AGENCY_LIST_KEY = 'direct';

/** The agencies a picker lists, A–Z, without the list's "Direct (no agency)" entry, so it isn't offered twice. */
export function agencyOptions(agencies: readonly MediaPlanAgency[]): MediaPlanAgency[] {
  return agencies.filter((a) => a.key !== DIRECT_AGENCY_LIST_KEY).sort((a, b) => a.name.localeCompare(b.name));
}

/** Where a rate came from, as the plan page and builder say it. */
export const RATE_BASIS_LABELS: Record<RateBasis, string> = {
  seller: 'Your rate',
  negotiated: 'Negotiated card',
  history: 'From history',
  list: 'List price',
};

export const GUARDRAIL_LABELS: Record<GuardrailKind, string> = {
  'below-list': 'Well below list',
  'below-history': 'Below what they paid',
  'above-list': 'Far above list',
};

/** Why a history reference didn't set the rate, by the check it failed. */
export const HISTORY_CHECK_WORDS: Array<[keyof UnitHistoryReference['checks'], string]> = [
  ['months', 'too few months'],
  ['impressions', 'too few paid impressions'],
  ['listRatio', 'too far from list'],
  ['brand', 'not bought by the brand itself'],
  ['inWindow', 'older than the 12 months before the flight'],
];

export const HISTORY_FLAG_WORDS: Record<HistoryReferenceFlag, string> = {
  'ctv-inferred': 'CTV read from all their :30s',
  'mix-unknown': 'some months had no format split',
};

/** "Nov 2025 – Sep 2026 · solid", or the window plus why it wasn't used. */
export function historyReferenceNote(h: UnitHistoryReference): string {
  if (h.solid) return `${h.windowLabel} · solid`;
  const failed = HISTORY_CHECK_WORDS.filter(([key]) => h.checks[key] === false).map(([, words]) => words);
  return `${h.windowLabel}${failed.length ? ` · not used: ${failed.join(', ')}` : ''}`;
}

/** Where a plan was built; 'script' plans say nothing. */
export const PLAN_SOURCE_LABELS: Partial<Record<MediaPlanDoc['source'], string>> = {
  ui: 'the Sales dashboard',
  chat: 'Culture Genius chat',
  mcp: 'Claude (connector)',
};

/** "$28.17 avg · 21 mo" from an advertiser's stored pricing summary, or "No paid history" (free impressions alone don't count). */
export function historyHint(summary: AdvertiserPricingSummary | undefined): string {
  if (!summary || summary.months <= 0 || summary.impressions <= 0 || summary.revenueUsd <= 0) return 'No paid history';
  return `${fmtUsd(summary.weightedEcpm)} avg · ${summary.months} mo`;
}

export const CONFIDENCE_VARIANT: Record<RateConfidence, StatusVariant> = {
  high: 'ok',
  medium: 'pending',
  low: 'flagged',
};

export const CONFIDENCE_LABELS: Record<RateConfidence, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

/** A pre-Oct 2026 rate suggestion's confidence, said as how much history stood behind it. */
export const HISTORY_STRENGTH_LABELS: Record<RateConfidence, string> = {
  high: 'Solid history',
  medium: 'Some history',
  low: 'Little history',
};

export const RATE_FLAG_LABELS: Record<string, string> = {
  'thin-evidence': 'Thin history — blended toward the category',
  'extreme-index': 'Unusual index vs category',
  'declining-trend': 'Rates sliding',
  'stale-history': 'Stale history',
  'resold-only': 'Resold history only',
  'suggested-category': 'Category unconfirmed',
  'negotiated-card': 'Negotiated card',
  'no-list-price': 'Missing list price',
  'mix-drift': 'Floors moved the blend',
  'thin-category': 'Thin category history',
  'raised-to-floor': 'Raised to floor',
  'raised-to-minimum': 'Raised to $10 minimum',
  'per-format-evidence': 'Per-format history used',
};

export const MAX_TIERS = 4;
export const MAX_SEGMENTS = 8;
